import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, Search } from "lucide-react";
import { PROFESSIONAL_MAP_SECTIONS } from "@/data/professionalMapData";
import { api } from "@/lib/api";
import { PROFESSIONAL_MAP_ARTWORK } from "@/data/professionalMapVisualAssets";
import ProfessionalMapHeroLinks from "@/components/professional-map/ProfessionalMapHeroLinks";

function CategoryVisual({ section }) {
  // Approved 23 October entrance photograph. Text and actions stay in the DOM.
  // 3:2 on mobile, fixed image band on desktop; no overlays or decorative badges.
  const src = PROFESSIONAL_MAP_ARTWORK.entryCards[section.slug];
  return (
    <div className="relative aspect-[3/2] overflow-hidden border-b border-[#6E5544]/10 bg-[#F1E8DC] sm:aspect-auto sm:h-[170px]"
      aria-hidden="true">
      <img src={src} alt="" loading="lazy" decoding="async"
        className="absolute inset-0 h-full w-full object-cover object-center"
        data-testid={`professional-map-entry-artwork-${section.slug}`} />
    </div>
  );
}

export default function ProfessionalMapLanding() {
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  // Global public search must never substitute a static category-title filter.
  // Only server-approved and consented published records may be shown here.
  const [searchState, setSearchState] = useState({ status: "idle", results: [], total: 0 });
  const searchRequest = useRef(0);

  async function submitSearch(event) {
    event.preventDefault();
    const q = query.trim();
    if (!q || searchState.status === "loading") return;
    const request = ++searchRequest.current;
    setSearchState({ status: "loading", results: [], total: 0 });
    try {
      const { data } = await api.get("/professional-map/search", { params: { q, limit: 12 } });
      if (request !== searchRequest.current) return;
      const rows = Array.isArray(data?.results) ? data.results.filter((item) =>
        item?.id && item?.public_name && (item?.entity_type === "person" || item?.entity_type === "business")
      ) : [];
      setSearchState({ status: "success", results: rows, total: Number(data?.total || 0) });
    } catch (_error) {
      if (request !== searchRequest.current) return;
      setSearchState({ status: "unavailable", results: [], total: 0 });
    }
  }

  return (
    <div
      className="min-h-screen text-[#3A2B22]"
      style={{ background: "linear-gradient(180deg, #F8F1E5 0%, #F6EEDF 46%, #F1E5D1 100%)" }}
      data-testid="professional-map-landing-page"
    >
      <header className="max-w-[1240px] mx-auto w-full px-5 sm:px-8 pt-4 sm:pt-5"
        data-testid="professional-map-top-navigation">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
          <button type="button" onClick={() => nav("/hall")}
            data-testid="professional-map-back-to-hall"
            className="inline-flex min-h-[44px] shrink-0 items-center gap-2 self-start rounded-lg px-2 -ml-2 hover:bg-white/45 transition-colors"
            aria-label="Вернуться в главный зал">
            <ChevronLeft className="w-4 h-4" strokeWidth={1.6} />
            <span className="font-serif text-sm">В Anokina Studio</span>
          </button>
          <form onSubmit={submitSearch} role="search" aria-label="Поиск по Профессиональной карте"
            className="relative flex w-full max-w-[480px] items-center rounded-xl border border-[#765944]/24 bg-white/72 shadow-[0_2px_14px_rgba(74,59,50,0.04)]"
            data-testid="professional-map-header-search">
            <Search className="ml-3 h-4 w-4 shrink-0 text-[#8A7565]" aria-hidden="true" strokeWidth={1.5} />
            <label className="sr-only" htmlFor="pm-global-search">Поиск по Профессиональной карте</label>
            <input id="pm-global-search" type="search" value={query}
              onChange={(event) => {
                searchRequest.current += 1;
                setQuery(event.target.value);
                setSearchState({ status: "idle", results: [], total: 0 });
              }}
              maxLength={180} autoComplete="off"
              placeholder="Мастер, кофейня, поставщик…"
              className="h-11 min-w-0 flex-1 bg-transparent px-3 text-sm text-[#49372C] outline-none placeholder:text-[#9A887B]"
              data-testid="professional-map-search" />
            <button type="submit" disabled={!query.trim() || searchState.status === "loading"}
              className="mr-1 min-h-[36px] shrink-0 rounded-lg border border-[#765944]/30 bg-[#FBF7EF]/95 px-3 text-xs font-medium text-[#594435] transition-colors hover:border-[#765944]/50 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#765944]/60 disabled:opacity-45"
              aria-label="Найти опубликованных участников">Найти</button>
          </form>
        </div>
        {searchState.status !== "idle" && (
          <section className="mt-3 rounded-2xl border border-[#765944]/20 bg-white/80 px-4 py-4 sm:ml-auto sm:max-w-[480px]"
            aria-label="Результаты поиска" aria-live="polite" data-testid="professional-map-search-results">
            {searchState.status === "loading" && <p className="text-sm text-[#705D4D]">Ищем опубликованные страницы…</p>}
            {searchState.status === "unavailable" && (
              <p className="text-sm text-[#705D4D]">
                Публичный поиск пока недоступен. Направления Профессиональной карты можно открыть ниже.
              </p>
            )}
            {searchState.status === "success" && (searchState.results.length === 0
              ? <p className="text-sm text-[#705D4D]">По запросу нет опубликованных профессиональных страниц.</p>
              : <div className="space-y-3">
                <p className="text-xs text-[#806B5A]">Опубликованные результаты</p>
                {searchState.results.map((item) => (
                  <button key={item.id} type="button" onClick={() => nav("/professional-map/profiles/" + encodeURIComponent(item.id))}
                    className="block w-full border-b border-[#765944]/15 pb-3 text-left last:border-0 last:pb-0 hover:bg-[#F8F1E7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#765944]" data-testid={"pm-search-open-" + item.id}
                    aria-label={"Открыть страницу: " + item.public_name}>
                    <p className="font-serif text-lg">{item.public_name}</p>
                    <p className="text-xs text-[#806B5A]">
                      {[item.city, item.country].filter(Boolean).join(", ")}
                    </p>
                    {item.description && <p className="mt-1 line-clamp-2 text-xs text-[#705D4D]">{item.description}</p>}
                  </button>
                ))}
              </div>)}
          </section>
        )}
      </header>

      <main className="max-w-[1240px] mx-auto px-5 sm:px-8 pb-16 sm:pb-20">
        <section
          className="relative mt-4 sm:mt-6 overflow-hidden rounded-[28px] border border-[#7B604B]/22 bg-[#F6EEE4] shadow-[0_14px_45px_rgba(74,59,50,0.07)]"
          data-testid="professional-map-hero"
        >
          {/* Approved image-only source; visible title/subtitle remain selectable UI text. */}
          <img
            src={PROFESSIONAL_MAP_ARTWORK.mainHeroMobile}
            alt=""
            className="block h-[240px] w-full object-cover object-[85%_center] sm:h-[320px] lg:hidden"
            data-testid="professional-map-hero-mobile-artwork"
          />
          <img
            src={PROFESSIONAL_MAP_ARTWORK.mainHeroDesktop}
            alt=""
            className="absolute inset-0 hidden h-full w-full object-cover lg:block"
            data-testid="professional-map-hero-desktop-artwork"
          />
          <div className="relative z-10 flex min-h-0 flex-col justify-center px-5 py-6 sm:px-9 sm:py-8 lg:min-h-[440px] lg:w-[64%] lg:px-12 lg:py-12">
            <div className="mb-2 text-[10px] uppercase tracking-[0.25em] text-[#876E5B] sm:mb-4">
              Anokina Studio
            </div>
            <h1
              className="max-w-3xl font-serif text-[clamp(28px,7.6vw,32px)] leading-[1.05] tracking-[-0.025em] sm:text-[46px] sm:leading-[1.02] lg:text-[52px] lg:leading-[0.98]"
              data-testid="professional-map-title"
            >
              Профессиональная карта
              <span className="block">кондитерской индустрии</span>
            </h1>
            <p className="mt-3 max-w-[30rem] font-serif text-[14px] leading-[1.45] text-[#5A473B] sm:mt-5 sm:text-lg sm:leading-6 lg:max-w-[470px] lg:text-base xl:max-w-[560px] xl:text-[17px]">
              Люди, поставщики, производство и профессиональные возможности
              <span className="block">в одном пространстве.</span>
            </p>
            <ProfessionalMapHeroLinks />
            <p className="mt-5 max-w-[530px] font-serif text-[clamp(22px,3vw,29px)] font-medium leading-[1.2] tracking-[-0.02em] text-[#49372C] sm:mt-6" data-testid="pm-hero-nearby-hint">
              Найти специалиста рядом с собой
            </p>
          </div>
        </section>

        <section className="mt-5 rounded-[24px] border border-[#765944]/20 bg-white/65 px-5 py-7 sm:px-8"
          aria-labelledby="professional-map-my-page-title" data-testid="professional-map-my-page-entry">
          <div className="flex flex-wrap items-center justify-between gap-5">
            <div className="max-w-[710px]">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#8A7463]">Участникам индустрии</p>
              <h2 id="professional-map-my-page-title" className="mt-2 font-serif text-2xl sm:text-3xl">
                Моя профессиональная страница
              </h2>
              <p className="mt-3 text-sm leading-6 text-[#715D4D]">
                Деятельность, примеры работ и контакты — всё в одном месте.
              </p>
            </div>
            <button type="button" onClick={() => nav("/account/professional-cards")}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-[#765944]/30 bg-[#FBF7EF]/90 px-5 py-3 text-sm font-medium text-[#594435] shadow-[0_2px_8px_rgba(74,59,50,0.025)] transition-colors hover:border-[#765944]/50 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#765944]/60"
              data-testid="professional-map-my-page-action">
              Открыть <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </section>

        {/* Real questions route and server data; no fake questions or placeholder links. */}
        <section className="mt-5 rounded-[24px] border border-[#765944]/20 bg-[#FBF7EF]/65 px-5 py-7 sm:px-8"
          aria-labelledby="professional-map-questions-title" data-testid="professional-map-questions-entry">
          <div className="flex flex-wrap items-center justify-between gap-5">
            <div className="max-w-[760px]">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#8A7463]">Знания и опыт коллег</p>
            <h2 id="professional-map-questions-title" className="mt-2 font-serif text-2xl sm:text-3xl">
              Профессиональные вопросы
            </h2>
            <p className="mt-3 text-sm leading-6 text-[#715D4D]">
              Вопросы о технологиях, работе и сотрудничестве. Практический опыт и ответы, к которым можно вернуться.
            </p>
            </div>
            <button type="button" onClick={() => nav("/professional-map/questions")}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-[#765944]/30 bg-[#FBF7EF]/90 px-5 py-3 text-sm font-medium text-[#594435] shadow-[0_2px_8px_rgba(74,59,50,0.025)] transition-colors hover:border-[#765944]/50 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#765944]/60"
              data-testid="professional-map-questions-action">
              Открыть <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </section>

        <section className="pt-7 sm:pt-9" aria-labelledby="professional-map-directions">
          <div className="mb-4 sm:mb-5">
            <h2 id="professional-map-directions" className="font-serif text-2xl sm:text-3xl mt-1">
              Вся индустрия в одной системе
            </h2>
          </div>

          {PROFESSIONAL_MAP_SECTIONS.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {PROFESSIONAL_MAP_SECTIONS.map((section) => (
                <button
                  key={section.slug}
                  type="button"
                  onClick={() => nav(`/professional-map/${section.slug}`)}
                  className="group overflow-hidden rounded-[20px] border border-[#765944]/24 bg-[#FBF7EF]/76 text-left shadow-[0_4px_18px_rgba(74,59,50,0.05)] hover:-translate-y-0.5 hover:bg-white/82 hover:shadow-[0_9px_24px_rgba(74,59,50,0.08)] transition-all"
                  data-testid={`professional-map-card-${section.slug}`}
                >
                  <CategoryVisual section={section} />
                  <div className="px-4 py-4">
                    <div className="text-[8px] uppercase tracking-[0.17em] text-[#917967] min-h-[20px]">
                      {section.eyebrow}
                    </div>
                    <h3 className="font-serif text-[17px] leading-[1.12] min-h-[38px]">
                      {section.title}
                    </h3>
                    <p className="mt-2 text-[11px] leading-[1.45] text-[#7B6859] line-clamp-3 min-h-[48px]">
                      {section.description}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-[#624B3A]">
                      Открыть раздел
                      <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" strokeWidth={1.4} />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-[#765944]/18 bg-white/45 px-5 py-8 text-center text-sm text-[#7B6859]">
              По запросу ничего не найдено.
            </div>
          )}
        </section>


      </main>
    </div>
  );
}
